import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import {
  DriveTimeOptimizerQuery,
  useDriveTimeOptimizerLazyQuery,
} from "~/graphql/__generated__/graphql";
import { useState } from "react";

import {
  assertDriveOptimizerCanRun,
  buildDriveOptimizerRequestDataFromInitialStateJson,
} from "~/utils/driveTimeOptimizerReplay";

type OptimizerReplayMenuItemProps = {
  open: boolean;
  onClose: () => void;
};

const textAreaSx = {
  "& .MuiInputBase-root": {
    alignItems: "flex-start",
    height: 320,
    overflow: "hidden",
  },
  "& textarea": {
    fontFamily: "monospace",
    height: "100% !important",
    overflow: "auto !important",
  },
};

export default function OptimizerReplayMenuItem({ open, onClose }: OptimizerReplayMenuItemProps) {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [outputLabel, setOutputLabel] = useState("Output");
  const [error, setError] = useState<string | null>(null);
  const [buildingOutput, setBuildingOutput] = useState(false);
  const [runningOptimizer, setRunningOptimizer] = useState(false);
  const [runOptimizerQuery] = useDriveTimeOptimizerLazyQuery({
    fetchPolicy: "network-only",
  });

  const handleReplay = async () => {
    setBuildingOutput(true);
    setError(null);

    try {
      const replayedRequestData = buildDriveOptimizerRequestDataFromInitialStateJson(input);
      setOutput(JSON.stringify(replayedRequestData, null, 2));
      setOutputLabel("Optimizer Request JSON");
    } catch (replayError) {
      setOutput("");
      setError(replayError instanceof Error ? replayError.message : "Failed to replay optimizer data.");
    } finally {
      setBuildingOutput(false);
    }
  };

  const handleRunOptimizer = async () => {
    setRunningOptimizer(true);
    setError(null);

    try {
      const replayedRequestData = buildDriveOptimizerRequestDataFromInitialStateJson(input);
      assertDriveOptimizerCanRun(replayedRequestData);

      const result = await runOptimizerQuery({
        variables: {
          input: replayedRequestData.input,
        },
      });

      setOutput(
        JSON.stringify(
          (result.data as DriveTimeOptimizerQuery | undefined)?.driveTimeOptimizer ?? null,
          null,
          2
        )
      );
      setOutputLabel("Optimizer Result");
    } catch (runError) {
      setOutput("");
      setError(runError instanceof Error ? runError.message : "Failed to run optimizer.");
    } finally {
      setRunningOptimizer(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Replay Optimizer JSON</DialogTitle>
      <DialogContent sx={{ overflow: "hidden" }}>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="OptimizerInitialState JSON"
              value={input}
              onChange={(event) => {
                setInput(event.target.value);
                setError(null);
              }}
              multiline
              fullWidth
              placeholder="Paste a JSON-stringified OptimizerInitialState here"
              sx={textAreaSx}
              slotProps={{
                htmlInput: {
                  "data-testid": "optimizer-replay-input",
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={outputLabel}
              value={output}
              multiline
              fullWidth
              sx={textAreaSx}
              slotProps={{
                input: {
                  readOnly: true,
                },
                htmlInput: {
                  "data-testid": "optimizer-replay-output",
                },
              }}
            />
          </Grid>
        </Grid>
        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={handleReplay}
          variant="outlined"
          disabled={buildingOutput || runningOptimizer || !input.trim()}
        >
          {buildingOutput ? <CircularProgress size={20} color="inherit" /> : "Generate Request"}
        </Button>
        <Button
          onClick={handleRunOptimizer}
          variant="contained"
          disabled={buildingOutput || runningOptimizer || !input.trim()}
        >
          {runningOptimizer ? <CircularProgress size={20} color="inherit" /> : "Run Optimizer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
